"use client";

import { analiticaInstance } from "@/services/analitica";

const generalAnalitica = analiticaInstance.eventFactory("general", {
  view: {
    notFound: () => ({
      label: "error-viewed",
      metadata: {
        screen: "Vitrine inexistente",
        reference: "Acesso à uma Vitrine que não existe",
      },
    }),
    error: () => ({
      label: "error-viewed",
      metadata: {
        screen: "Erro inesperado",
        reference: "Visualização de tela de erro genérico",
      },
    }),
  },
});

const storeAnalitica = analiticaInstance.eventFactory("store", {
  view: {
    pageViewed: (hasFilter: boolean, hasWhatsapp: boolean) => ({
      label: "page-viewed",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Visualização da listagem da Vitrine",
        "has filter": hasFilter ? "Yes" : "No",
        "has whatsapp": hasWhatsapp ? "Yes" : "No",
      },
    }),
    searchedAndNotFound: () => ({
      label: "page-viewed",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Visualização de uma busca que não retornou produtos",
      },
    }),
    empty: () => ({
      label: "page-viewed",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference:
          "Visualização de uma vitrine que não possui produtos cadastrados",
      },
    }),
    error: () => ({
      label: "error-viewed",
      metadata: {
        screen: "Erro inesperado",
        reference:
          "Visualização de tela de erro genérico na listagem de produtos",
      },
    }),
  },
  action: {
    searched: () => ({
      label: "product-searched",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Uma busca foi realizada na listagem de produtos",
      },
    }),
    emptyWhatsappClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference:
          "Clique no botão de whatsapp em uma vitrine que não possui produtos cadastrados",
        "button reference": "Conversar com loja sem produto",
      },
    }),
    categoryClicked: (isAllCategories: boolean) => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        "button reference": "Filtro de categoria",
        "all categories": isAllCategories ? "Yes" : "No",
      },
    }),
    orderClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Clique no seletor de ordenação na listagem de produtos",
        "button reference": "Filtro de ordenação",
      },
    }),
    clearFiltersClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Clique no botão de limpar filtros na listagem de produtos",
        "button reference": "Limpar filtros",
      },
    }),
    productClicked: () => ({
      label: "list-item-selected",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Clique em um item da listagem de produtos",
        "item reference": "Card de produto",
      },
    }),
    errorWhatsappClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference:
          "Clique no botão de conversa com a loja via whatsapp na tela de erro genérico",
        "button reference": "Conversar com a loja com erro genérico",
      },
    }),
  },
  share: {
    whatsappHeader: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Botão comprar na Home da Vitrine",
      },
    }),
    whatsappFloating: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Botão de Whatsapp na vitrine",
      },
    }),
  },
});

const productAnalitica = analiticaInstance.eventFactory("product", {
  view: {
    pageViewed: ({
      hasWhatsapp,
      hasImage,
      hasVariations,
      hasDescription,
    }: {
      hasWhatsapp: boolean;
      hasImage: boolean;
      hasVariations: boolean;
      hasDescription: boolean;
    }) => ({
      label: "page-viewed",
      metadata: {
        "has whatsapp": hasWhatsapp ? "Yes" : "No",
        "has image": hasImage ? "Yes" : "No",
        "has variations": hasVariations ? "Yes" : "No",
        "has description": hasDescription ? "Yes" : "No",
        screen: "Página de produto",
        reference: "Visualização de um produto específico da loja",
      },
    }),
    notFound: () => ({
      label: "error-viewed",
      metadata: {
        screen: "Página de produto inexistente",
        reference: "Acesso à página de um produto que não existe",
      },
    }),
  },
  action: {
    searchClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference:
          "Clique no botão de busca na barra superior na página de produto",
        "button reference": "Busca de produto",
      },
    }),
    homeClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no ícone da loja na página de produto",
        "button reference": "Home",
      },
    }),
    breadcrumbClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no breadcrumbs na página de produto",
        "button reference": "Breadcrumbs",
      },
    }),
    variationClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no seletor de alguma variação na página de produto",
        "button reference": "Seletor de variação",
      },
    }),
    relatedClicked: () => ({
      label: "list-item-selected",
      metadata: {
        screen: "Página de produto",
        reference:
          "Clique em um produto na seção de recomendações na página de produto",
        "item reference": "Card de produto",
      },
    }),
    otherProductsClicked: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto inexistente",
        reference:
          "Clique no botão de ver outros produtos na tela de produto inexistente",
        "button reference": "Outros produtos",
      },
    }),
  },
  share: {
    whatsappHeader: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no botão de whatsapp na barra de navegação",
        "button reference": "Botão Whatsapp geral",
      },
    }),
    whatsapp: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no botão de pedir via whatsapp na página de produto",
        "button reference": "Botão Whatsapp de produto",
      },
    }),
    link: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no botão de compartilhamento de produto",
        "button reference": "Compartilhamento de produto",
      },
    }),
  },
});

const aboutAnalitica = analiticaInstance.eventFactory("store", {
  view: {
    pageViewed: () => ({
      label: "page-viewed",
      metadata: {
        screen: "Página Sobre",
        reference: "Visualização da página Sobre do Catálogo Online",
      },
    }),
  },
});

const cartAnalitica = analiticaInstance.eventFactory("cart", {
  action: {
    headerClicked: (page: string) => ({
      label: "button-clicked",
      metadata: {
        screen: page,
        reference: "Clique no botão de carrinho do header",
        description:
          "Clique no botão de carrinho do header da página de listagem de produtos",
        "button reference": "Carrinho",
      },
    }),
    sendToWhatsapp: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Carrinho",
        reference: "Clique no botão de finalizar pedido na drawer do carrinho",
        description:
          "Clique no botão de Finalizar no WhatsApp no drawer de carrinho",
        "button reference": "Finalizar",
      },
    }),
    addToCart: () => ({
      label: "button-clicked",
      metadata: {
        screen: "Página de produto",
        reference: "Clique no botão de adicionar produto ao carrinho",
        description: "Clique em adicionar produto ao carrinho",
        "button reference": "Adicionar ao carrinho",
      },
    }),
    addToCartByList: (
      buttonReference: "Adicionar" | "Mais opções" = "Adicionar"
    ) => ({
      label: "button-clicked",
      metadata: {
        screen: "Página inicial da Vitrine",
        reference: "Clique em adicionar produto ao carrinho na listagem",
        description:
          'Usuário clicou no botão de "Adicionar" ao carrinho dentro de um item na listagem de produtos',
        "button reference": buttonReference,
      },
    }),
  },
});

export {
  generalAnalitica,
  storeAnalitica,
  productAnalitica,
  aboutAnalitica,
  cartAnalitica,
};
